import {
  PageContainer,
  PageHeader,
  Toolbar,
} from "../../../components/Layout/PageShell";
import { RootState, useAppDispatch, useAppSelector } from "../../../store";
import { useEffect, useState } from "react";
import { apiDelete, apiGet, apiPost } from "../../../components/Common/Axios";
import toast from "react-hot-toast";
import SchoolSalesPopup from "./SchoolSalesPopup";
import { Button } from "components/ui/button";
import SchoolListPage from "./SchoolList";
import { Gugun, School, SchoolSales, Sido } from "lib/models";
import { DisplayYears, UserRole } from "lib/types";
import { setMenuIndex } from "store/userSlice";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "components/ui/tabs";
import { Input } from "components/ui/input";
import { cn, getCount } from "lib/utils";
import { setSchoolYears } from "store/mainSlice";

const schoolGradeCnts = [6, 3, 3, 0, 0];
const schoolTypeNames = [
  "초등학교",
  "중학교",
  "고등학교",
  "대학교",
  "기타기관",
];

function SchoolSearch() {
  const [sidoList, setSidoList] = useState([]);
  const [selectedSido, setSelectedSido] = useState<Sido | undefined>(undefined);
  const [gugunList, setGugunList] = useState<Gugun[]>([]);
  const [selectedGugun, setSelectedGugun] = useState<Gugun | undefined>(
    undefined
  );

  // 리스트
  const [schools, setSchools] = useState<School[][]>([[], [], [], [], []]);
  const [searchSchools, setSearchSchools] = useState<School[]>([]);
  const [selectedSchool, setSelectedSchool] = useState<School | undefined>(
    undefined
  );
  const [selectedSchoolTab, setSelectedSchoolTab] = useState("0");

  const [selectedSchoolSales, setSelectedSchoolSales] = useState<
    SchoolSales | undefined
  >(undefined);

  const [openSales, setOpenSales] = useState(false);
  // const [salesCnts, setSalesCnts] = useState<number[]>([0, 0, 0, 0, 0]);
  const [contractCnts, setContractCnts] = useState<number[]>([0, 0, 0, 0, 0]);

  const [searchKeyword, setSearchKeyword] = useState("");
  const [isSearchMode, setIsSearchMode] = useState(false);

  const me = useAppSelector((state: RootState) => state.user.me);
  const schoolYears = useAppSelector(
    (state: RootState) => state.main.schoolYears
  );

  const loadSidoList = async () => {
    const response = await apiGet("/sido");
    if (response.error || !response.data) {
      toast.error("시도 정보를 불러오는데 실패했습니다.");
      console.error("loadSidoList error", response.error);
      return;
    }

    const _sidoList = response.data;
    const newSidoList = _sidoList.map((sido: Sido) => ({
      ...sido,
      guguns: [
        ...sido.guguns,
        { id: 0, name: "전체", sidoId: sido.id, sido: sido },
      ],
      gugunCnts: [
        ...sido.gugunCnts,
        {
          id: 0,
          name: "전체",
          sidoId: sido.id,
          sido: sido,
          contractCnts: [],
          contractCntsE: [],
          contractCntsM: [],
          contractCntsH: [],
          contractCntsO: [],
        },
      ],
    }));

    setSidoList(newSidoList);
    setSelectedSido(newSidoList[0]);
  };

  const loadSchoolList = async (sidoId: number, gugunId: number) => {
    const response = await apiGet(`/school/${sidoId}/${gugunId}`);
    if (response.error) {
      toast.error("학교 목록을 불러오는데 실패했습니다.");
      console.error("loadSchoolList error", response.error);

      return;
    }
    const newSchools = response.data;
    setSchools(newSchools);

    const selectedTab = Number(selectedSchoolTab);

    if (
      selectedTab >= 0 &&
      selectedTab <= 4 &&
      newSchools[selectedTab].length > 0
    ) {
      setSelectedSchool(response.data[selectedTab][0]);
    } else {
      setSelectedSchool(undefined);
    }
  };

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(setMenuIndex(1));
    loadSidoList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 시도 변경시 구군 리스트 갱신
  useEffect(() => {
    if (selectedSido === undefined) {
      setGugunList([]);
    } else {
      //setGugunList(selectedSido.gugunCnts);
      // const selectedGugun = selectedSido.gugunCnts[0];
      setGugunList(selectedSido.gugunCnts);
      const selectedGugun = selectedSido.gugunCnts[0];
      setSelectedGugun(selectedGugun);
    }
  }, [selectedSido]);

  // 구군변경시 학교 리스트 갱신
  useEffect(() => {
    if (selectedGugun === undefined) {
      setSchools([[], [], [], [], []]);
    } else {
      loadSchoolList(selectedGugun.sidoId, selectedGugun.id);
      // setSalesCnts([
      //   selectedGugun.salesCntE,
      //   selectedGugun.salesCntM,
      //   selectedGugun.salesCntH,
      //   selectedGugun.salesCntU,
      //   selectedGugun.salesCntO,
      // ]);
      setContractCnts([
        getCount(selectedGugun.contractCntsE, schoolYears),
        getCount(selectedGugun.contractCntsM, schoolYears),
        getCount(selectedGugun.contractCntsH, schoolYears),
        getCount(selectedGugun.contractCntsU, schoolYears),
        getCount(selectedGugun.contractCntsO, schoolYears),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGugun, schoolYears]);

  // 시도 클릭
  const handleSidoClick = (sido: Sido) => {
    setSelectedSido(sido);
  };

  // 구군 클릭
  const handleGugunClick = (gugun: Gugun) => {
    setSelectedGugun(gugun);
  };

  // 팝업에서 SchoolSales 저장
  const onSaveSchoolSalesCompleted = async (
    schoolSales: SchoolSales,
    isNewMode: boolean
  ) => {
    if (!selectedSchool) {
      console.error("selectedSchool === undefined", { selectedSchool });
      return false;
    }

    let schoolSalesList = [] as SchoolSales[];
    if (isNewMode) {
      // 신규
      schoolSalesList = [...selectedSchool.schoolSales, schoolSales];
    } else {
      // 수정
      schoolSalesList = selectedSchool.schoolSales.map((sales) =>
        sales.id === schoolSales.id ? schoolSales : sales
      );
    }

    const newSchool = {
      ...selectedSchool,
      schoolSales: schoolSalesList,
    } as School;

    const selectedTab = Number(selectedSchoolTab);

    setSelectedSchool(newSchool);
    setSchools(
      schools.map((schoolList, index) =>
        index !== selectedTab
          ? schoolList
          : schoolList.map((sc) =>
              sc.id === selectedSchool.id ? newSchool : sc
            )
      )
    );

    return true;
  };

  const onDeleteSchoolSales = async (
    schoolSales: SchoolSales
  ): Promise<boolean> => {
    if (!selectedSchool) {
      console.error("학교가 선택되지 않았습니다. selectedSchool === undefined");
      return false;
    }

    const response = await apiDelete(`/school/sales/${schoolSales.id}`);

    if (response.error) {
      toast.error("삭제 실패");
      console.error("삭제 실패", response.error);
      return false;
    }

    const sales = selectedSchool.schoolSales.filter(
      (sales) => sales.id !== schoolSales.id
    );
    const newSchool = {
      ...selectedSchool,
      schoolSales: sales,
    } as School;

    setSelectedSchool(newSchool);

    const selectedTab = Number(selectedSchoolTab);

    setSchools(
      schools.map((schoolList, index) =>
        index !== selectedTab
          ? schoolList
          : schoolList.map((sc) =>
              sc.id === selectedSchool.id ? newSchool : sc
            )
      )
    );

    return true;
  };

  const searchSchool = async () => {
    const response = await apiPost("/school/search", {
      searchKeyword,
    });
    if (response.error) {
      toast.error("학교 검색에 실패했습니다.");
      console.error("searchSchool error", response.error);
      return;
    }
    setSearchSchools(response.data);
    setIsSearchMode(true);
    setSelectedSchoolTab("5");
  };

  // 팝업
  // sales = undefined :학교 클릭시
  // sales !== undefined : 판매정보 클릭시
  const popupSchoolSales = (
    e: any,
    school: School,
    sales?: SchoolSales | undefined
  ) => {
    if (e.target.closest("a")) {
      return;
    }

    if (
      sales !== undefined &&
      sales.userId !== me.id &&
      me.role < UserRole.MANAGER
    ) {
      toast.error("본인이 등록한 수업만 수정할 수 있습니다.");
      return;
    }

    setSelectedSchool(school);
    setSelectedSchoolSales(sales);
    setOpenSales(true);
  };

  const handleYearClick = (year: number) => {
    if (schoolYears.includes(year)) {
      dispatch(setSchoolYears(schoolYears.filter((y) => y !== year)));
    } else {
      dispatch(setSchoolYears([...schoolYears, year]));
    }
  };

  return (
    <PageContainer>
      <SchoolSalesPopup
        open={openSales}
        onOpenChange={setOpenSales}
        schoolSales={selectedSchoolSales}
        school={selectedSchool}
        onClose={() => setOpenSales(false)}
        onSaveCompleted={onSaveSchoolSalesCompleted}
        onDelete={onDeleteSchoolSales}
        isEnableNew={true}
        isShowDelete={true}
        accordionStates={["item-1", "item-2", "item-3"]}
      />
      <PageHeader
        title="학교검색"
        description="지역과 학교급으로 학교를 찾고 영업 정보를 등록합니다."
      />
      <div className="grid grid-cols-1 md:grid-cols-[180px_160px_minmax(0,1fr)] gap-4 text-sm">
        <div className="md:sticky md:top-4 md:self-start">
          <div className="hidden md:block px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            시도
          </div>

          <div className="flex md:block overflow-x-auto md:overflow-y-auto md:max-h-[calc(100vh-10rem)] rounded-xl border border-solid border-gray-200">
            {sidoList.map((sido: Sido) => (
              <div
                className={`px-3 py-2 cursor-pointer whitespace-nowrap shrink-0 ${
                  sido === selectedSido
                    ? "bg-indigo-50 font-semibold text-indigo-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                key={sido.id}
                onClick={() => handleSidoClick(sido)}
              >
                {sido.name}{" "}
                {getCount(sido.contractCnts, schoolYears) > 0 && (
                  <span className="text-gray-400">
                    ({getCount(sido.contractCnts, schoolYears)})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="md:sticky md:top-4 md:self-start">
          <div className="hidden md:block px-1 pb-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            구군
          </div>
          <div className="flex md:block overflow-x-auto md:overflow-y-auto md:max-h-[calc(100vh-10rem)] rounded-xl border border-solid border-gray-200">
            {gugunList.map((gugun: Gugun) => (
              <div
                className={`px-3 py-2 cursor-pointer whitespace-nowrap shrink-0 ${
                  gugun === selectedGugun
                    ? "bg-indigo-50 font-semibold text-indigo-700"
                    : "bg-white text-gray-700 hover:bg-gray-50"
                }`}
                key={gugun.id}
                onClick={() => handleGugunClick(gugun)}
              >
                {gugun.name}{" "}
                {getCount(gugun.contractCnts, schoolYears) > 0 && (
                  <span className="text-gray-400">
                    ({getCount(gugun.contractCnts, schoolYears)})
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        <div className="flex min-w-0 flex-col">
          <Tabs
            value={selectedSchoolTab}
            onValueChange={setSelectedSchoolTab}
            className="flex-1"
          >
            <Toolbar className="mb-3 justify-between">
              <TabsList className="flex w-full xl:w-auto xl:flex-1 justify-start overflow-x-auto">
                {schoolTypeNames.map((schoolTypeName, index) => (
                  <TabsTrigger
                    key={index}
                    value={index.toString()}
                    className="flex-1 shrink-0 px-2"
                  >
                    {schoolTypeName}{" "}
                    {contractCnts[index] > 0 && (
                      <span>({contractCnts[index]})</span>
                    )}
                  </TabsTrigger>
                ))}
                {isSearchMode && (
                  <TabsTrigger value="4" className="flex-1 shrink-0 px-2">
                    검색결과
                  </TabsTrigger>
                )}
              </TabsList>
              <div className="flex flex-wrap items-center gap-2">
                {DisplayYears.map((year) => (
                  <div
                    key={year}
                    className={cn(
                      "text-xs rounded-md py-2 px-3 cursor-pointer",
                      schoolYears.includes(year)
                        ? "bg-indigo-600 text-white border border-indigo-600 shadow-sm"
                        : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
                    )}
                    onClick={() => handleYearClick(year)}
                  >
                    {year}년
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 w-full xl:w-auto">
                <div className="flex-1 xl:flex-none">
                  <Input
                    type="text"
                    value={searchKeyword}
                    placeholder="학교 이름 검색"
                    className="px-2 border border-solid border-gray-300 rounded-md w-full xl:w-56 h-9 bg-white"
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        searchSchool();
                        setSelectedSchoolTab("4");
                      }
                    }}
                  />
                </div>
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => searchSchool()}
                >
                  검색
                </Button>
              </div>
            </Toolbar>
            <div className="">
              {schools.map((schoolList, index) => (
                <TabsContent key={index} value={index.toString()}>
                  <SchoolListPage
                    schoolList={schoolList}
                    schoolGradeCnt={schoolGradeCnts[index]}
                    popupSchoolSales={popupSchoolSales}
                    me={me}
                    years={schoolYears}
                  />
                </TabsContent>
              ))}
              <TabsContent value="5">
                <SchoolListPage
                  schoolList={searchSchools}
                  schoolGradeCnt={6}
                  popupSchoolSales={popupSchoolSales}
                  me={me}
                  years={schoolYears}
                />
              </TabsContent>
            </div>
          </Tabs>
        </div>
      </div>
    </PageContainer>
  );
}

export default SchoolSearch;
